/**
 * İÇERİK GÜNLÜĞÜ (Orkestra Şefi — Faz 2) — "bir cümle değişse bile" takip ekranı.
 * Her taramada sayfa gövdesi bir önceki sürümle cümle cümle karşılaştırılır;
 * bu ekran değişiklik zaman çizgisini gösterir: ne zaman, hangi sayfada, hangi
 * cümleler (eski → yeni), değişikliği kimin yaptığı (Geolop revizesi mi, sitede
 * elle mi). Her satırdan tek tıkla sayfa güçlendirmeye gidilir.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { GlPageHeader } from "@/components/ui/gl-kit";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { History, Loader2, ChevronDown, ChevronRight, Sparkles, Pencil, Wand2, Plus, Minus, Repeat } from "lucide-react";

type Ozet = { eklenen: number; silinen: number; degisen: number; toplamEski: number; toplamYeni: number };
type Fark = { eklenen: string[]; silinen: string[]; degisen: { eski: string; yeni: string }[] };
type Satir = { id: number; pageId: number; url: string; kaynak: "site" | "geolop-revize"; ozet: Ozet | null; createdAt: string; title: string | null };

const kisaUrl = (u: string) => u.replace(/^https?:\/\/[^/]+/, "") || "/";

function FarkGorunumu({ domainId, vid }: { domainId: number; vid: number }) {
  const detay = useQuery<{ fark: Fark | null }>({
    queryKey: ["icerik-gunlugu-detay", domainId, vid],
    queryFn: async () => (await fetch(`/api/domains/${domainId}/icerik-gunlugu/${vid}`)).json(),
  });
  if (detay.isLoading) return <div className="p-3 text-[12px] text-muted-foreground"><Loader2 className="mr-1.5 inline h-3.5 w-3.5 animate-spin" />Fark yükleniyor…</div>;
  const f = detay.data?.fark;
  if (!f) return <div className="p-3 text-[12px] text-muted-foreground">Fark detayı bulunamadı.</div>;
  return (
    <div className="space-y-2.5 border-t p-3 dark:border-white/10">
      {f.degisen.map((d, i) => (
        <div key={`d${i}`} className="rounded-lg border border-amber-500/25 bg-amber-500/[0.05] p-2.5 text-[12.5px] leading-relaxed">
          <div className="mb-1 flex items-center gap-1 text-[10.5px] font-bold uppercase text-amber-600 dark:text-amber-400"><Repeat className="h-3 w-3" /> Değişti</div>
          <p className="text-muted-foreground line-through decoration-red-400/60">{d.eski}</p>
          <p className="mt-1">{d.yeni}</p>
        </div>
      ))}
      {f.eklenen.map((c, i) => (
        <div key={`e${i}`} className="rounded-lg border border-emerald-500/25 bg-emerald-500/[0.05] p-2.5 text-[12.5px] leading-relaxed">
          <div className="mb-1 flex items-center gap-1 text-[10.5px] font-bold uppercase text-emerald-600 dark:text-emerald-400"><Plus className="h-3 w-3" /> Eklendi</div>
          <p>{c}</p>
        </div>
      ))}
      {f.silinen.map((c, i) => (
        <div key={`s${i}`} className="rounded-lg border border-red-500/25 bg-red-500/[0.05] p-2.5 text-[12.5px] leading-relaxed">
          <div className="mb-1 flex items-center gap-1 text-[10.5px] font-bold uppercase text-red-600 dark:text-red-400"><Minus className="h-3 w-3" /> Silindi</div>
          <p className="text-muted-foreground line-through decoration-red-400/60">{c}</p>
        </div>
      ))}
    </div>
  );
}

export default function IcerikGunlugu() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const [acik, setAcik] = useState<number | null>(null);
  const [location] = useLocation();
  // /icerik-gunlugu?pageId=123 derin linki (Fırsatlar Merkezi'nden gelir)
  const pageIdFiltre = useMemo(() => {
    const m = /[?&]pageId=(\d+)/.exec(typeof window !== "undefined" ? window.location.search : location);
    return m ? Number(m[1]) : null;
  }, [location]);

  const gunluk = useQuery<{ satirlar: Satir[]; izlenenSayfa: number; toplamDegisiklik: number }>({
    queryKey: ["icerik-gunlugu", activeDomainId, pageIdFiltre],
    enabled: !!activeDomainId,
    staleTime: 60_000,
    queryFn: async () =>
      (await fetch(`/api/domains/${activeDomainId}/icerik-gunlugu${pageIdFiltre ? `?pageId=${pageIdFiltre}` : ""}`)).json(),
  });

  const satirlar = gunluk.data?.satirlar ?? [];

  return (
    <div className="mx-auto max-w-5xl p-4 md:p-6">
      <GlPageHeader
        title="İçerik Günlüğü"
        desc="Sitedeki her içerik değişikliği cümle düzeyinde loglanır: ne zaman, hangi sayfada, hangi cümleler. Geolop revizesinin yayına girdiği de buradan görülür; 6 ay geriye saklanır."
        icon={<History className="h-5 w-5" />}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
        <span className="rounded-full bg-gray-100 px-2.5 py-1 dark:bg-white/[0.06]">{gunluk.data?.izlenenSayfa ?? 0} sayfa izleniyor</span>
        <span className="rounded-full bg-gray-100 px-2.5 py-1 dark:bg-white/[0.06]">{gunluk.data?.toplamDegisiklik ?? 0} değişiklik kaydı</span>
        {pageIdFiltre && (
          <Link href="/icerik-gunlugu"><Button size="sm" variant="outline">Filtreyi kaldır (tek sayfa gösteriliyor)</Button></Link>
        )}
      </div>

      {gunluk.isLoading ? (
        <div className="py-16 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />Günlük okunuyor…</div>
      ) : !satirlar.length ? (
        <Card><CardContent className="p-8 text-center">
          <History className="mx-auto mb-2 h-8 w-8 text-gray-300" />
          <p className="font-semibold">Henüz kayıtlı değişiklik yok</p>
          <p className="mx-auto mt-1 max-w-md text-[12.5px] text-muted-foreground">
            İlk tarama sessiz taban çizgisini oluşturur; sonraki taramalarda içerikte değişen her cümle burada listelenir.
            Sayfaları yeniden taradıkça günlük dolar.
          </p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {satirlar.map((r) => {
            const revize = r.kaynak === "geolop-revize";
            const o = r.ozet;
            const acikMi = acik === r.id;
            return (
              <Card key={r.id}><CardContent className="p-0">
                {/* div+role: satırın içinde gerçek buton (güçlendir) var — button içinde button geçersiz HTML */}
                <div role="button" tabIndex={0} className="flex w-full cursor-pointer items-center gap-3 p-3.5 text-left"
                  onClick={() => setAcik(acikMi ? null : r.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setAcik(acikMi ? null : r.id); } }}>
                  {acikMi ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${revize ? "bg-violet-500/15 text-violet-600 dark:text-violet-400" : "bg-sky-500/15 text-sky-600 dark:text-sky-400"}`}>
                        {revize ? <Sparkles className="h-3 w-3" /> : <Pencil className="h-3 w-3" />}
                        {revize ? "Geolop revizesi yayında" : "Sitede değişiklik"}
                      </span>
                      <span className="truncate text-[13.5px] font-semibold">{r.title || kisaUrl(r.url)}</span>
                      <span className="truncate text-[11.5px] text-muted-foreground">{kisaUrl(r.url)}</span>
                    </div>
                    {o && (
                      <p className="mt-0.5 flex flex-wrap gap-x-3 text-[12px] text-muted-foreground">
                        {o.degisen > 0 && <span className="text-amber-600 dark:text-amber-400">~{o.degisen} değişti</span>}
                        {o.eklenen > 0 && <span className="text-emerald-600 dark:text-emerald-400">+{o.eklenen} eklendi</span>}
                        {o.silinen > 0 && <span className="text-red-600 dark:text-red-400">−{o.silinen} silindi</span>}
                        <span>{o.toplamEski} → {o.toplamYeni} cümle</span>
                      </p>
                    )}
                  </div>
                  <span className="shrink-0 text-[11.5px] text-muted-foreground">
                    {new Date(r.createdAt).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </span>
                  <Link href={`/page-strengthen/${r.pageId}`} onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="outline" className="shrink-0 gap-1.5"><Wand2 className="h-3.5 w-3.5" /> Bu haliyle güçlendir</Button>
                  </Link>
                </div>
                {acikMi && activeDomainId && <FarkGorunumu domainId={activeDomainId} vid={r.id} />}
              </CardContent></Card>
            );
          })}
        </div>
      )}

      <p className="mt-3 text-[11px] text-muted-foreground">
        Karşılaştırma her taramada bir önceki sürümle yapılır (deterministik, AI maliyeti yok). İçeriğin üçte birinden fazlası
        silinirse Nöbetçi uyarır ve Fırsatlar Merkezi'ne "içerik kırpıldı" sinyali düşer. Kayıtlar 6 ay saklanır.
      </p>
    </div>
  );
}
